/**
 * Page context reported to the Control Plane so Zaviago AI knows what page the
 * user is looking at (Plan 1968 Feature 4).
 *
 * Contract: CP `control_plane.api.ai.set_page_context` (Plan 1968 Feature 3) keeps
 * only the top-level keys below, caps strings at 300 chars (screen.text 4000),
 * list.selectedIds at 50, other lists at 20 (screen lists: see the screen caps) and
 * rejects payloads over 32 KB (F13; was 8 KB).
 *
 * NEVER put in here: tokens, unsaved form values, full page data, other customers'
 * personal data beyond the open record's title/key fields.
 */

export const PAGE_CONTEXT_VERSION = 1;

export const PAGE_CONTEXT_LIMITS = {
  /** Server rejects > 32768 bytes; keep headroom. */
  maxBytes: 31000,
  maxString: 300,
  maxSelectedIds: 50,
  maxListItems: 20,
  maxRecent: 3,
  maxKeyFields: 8,
  maxFieldValueChars: 120,
  /** screen.text (text fallback) — the only string allowed past maxString */
  maxScreenText: 4000,
  maxScreenSections: 12,
  maxScreenKpis: 30,
  maxScreenCharts: 10,
  maxScreenFields: 60,
  maxScreenRows: 20,
  maxScreenColumns: 12,
  /** fitPageContext: fields kept per section once the payload is over budget */
  screenFieldsWhenShrunk: 30,
} as const;

export type PageType =
  | "detail"
  | "create"
  | "list"
  | "dashboard"
  | "settings"
  | "editor"
  | "app"
  | "tool"
  | "other";

/** What the URL alone tells us about the page (see resolvePageRoute). */
export interface ResolvedPageRoute {
  pageType: PageType;
  /** The react-router pattern that matched, e.g. `/inventory/item/:name`; undefined = unknown page */
  pattern?: string;
  doctype?: string;
  /** Record id from the URL (doc name, or a non-doctype id such as a ticket number) */
  name?: string;
  /** For non-doctype records: which route param `name` came from (e.g. "ticketNo") */
  recordRef?: string;
}

export type KeyFieldValue = string | number | boolean;

/** The open record — SAVED values only (never unsaved form edits). */
export interface RecordContext {
  doctype?: string;
  name?: string;
  title?: string;
  isNew?: boolean;
  docstatus?: number;
  status?: string;
  /** Non-doctype records: the route param the name came from */
  ref?: string;
  /** A few key fields chosen from meta (title_field, status, in_list_view), values truncated */
  fields?: Record<string, KeyFieldValue>;
}

export interface ListFilterContext {
  field: string;
  operator: string;
  value: unknown;
}

export interface ListContext {
  doctype: string;
  filters: ListFilterContext[];
  search?: string;
  sort?: { field: string; desc: boolean };
  page: number;
  pageSize: number;
  total?: number;
  viewMode?: string;
  /** Ticked row names (doc names), capped at 50 — see selectedCount for the real number */
  selectedIds: string[];
  selectedCount: number;
  /** Record open in the list's side panel (?detail=) */
  openDetail?: string;
}

export interface PageRef {
  path: string;
  pageType: PageType;
  doctype?: string;
  name?: string;
  title?: string;
  at: string;
}

/**
 * Screen snapshot (plan 1968 F13): WHAT the page shows, as display strings exactly as
 * the user sees them. Sent only while the Zaviago AI panel is open. Never unsaved
 * edits, never hidden tabs, never images.
 */
export interface ScreenKpi {
  label: string;
  value: string;
  change?: string;
  window?: string;
  note?: string;
}

export interface ScreenChart {
  title: string;
  kind?: string;
  /** One line computed from the data: points, peak/low with x label, last, total */
  summary: string;
}

export interface ScreenTable {
  title?: string;
  columns: string[];
  /** Each row = its visible cells joined with " | " */
  rows: string[];
  shown: number;
  total?: number;
}

export interface ScreenField {
  label: string;
  value: string;
  group?: string;
}

export interface ScreenSection {
  /** Stable publisher id, e.g. "business-dashboard.kpis" */
  id: string;
  title?: string;
  kpis?: ScreenKpi[];
  charts?: ScreenChart[];
  table?: ScreenTable;
  fields?: ScreenField[];
}

/** What a publisher provides (the section id comes from the publisher) */
export type ScreenSectionInput = Omit<ScreenSection, "id">;

export interface ScreenContext {
  /** Capture time (ISO) */
  at: string;
  /** components = structured publishers; text = main-content text fallback */
  source: "components" | "text";
  sections: ScreenSection[];
  /** Only when source = "text" */
  text?: string;
  /** Something was cut to fit */
  truncated?: boolean;
}

export interface PageContext {
  v: number;
  seq: number;
  at: string;
  /** Random id of this browser tab (several tabs can report; the last one wins) */
  tabId: string;
  path: string;
  search: string;
  pageType: PageType;
  doctype?: string;
  label?: string;
  title?: string;
  record?: RecordContext;
  activeTab?: { id: string; label?: string };
  list?: ListContext;
  ui: { language: string; isDesktop: boolean };
  /** tenant here and at top level is a MISMATCH GUARD only — the AI's tenant comes from its token */
  scope: { tenant: string | null; company: string | null; storefrontKey: string | null; storefrontLabel?: string };
  user: { email: string | null; isAdmin: boolean; role: string | null; appAccess: string[] | null };
  /** Previous pages, newest first (current page not included) */
  recent: PageRef[];
  timezone: string;
  tenant: string | null;
  /** App-specific context keyed by source, e.g. { "zaviago-sheets": {...} } — see usePageContextExtra */
  extra?: Record<string, unknown>;
  /** What the page shows (only while the AI panel is open) — see ScreenContext */
  screen?: ScreenContext;
}

/** Everything the reporter needs from the page; it adds v / seq / at / tabId / recent. */
export type PageSnapshot = Omit<PageContext, "v" | "seq" | "at" | "tabId" | "recent">;
